import { SupabaseTableBase } from '../../utils/SupabaseTableBase';
import { Call } from './types';

export class CallService extends SupabaseTableBase<Call> {
  constructor() {
    super('Call');
  }

  async startCall(userId?: string | null): Promise<Call | null> {
    const callData = {
      startDateTime: new Date().toISOString(),
      endDate: null,
      userId: userId ?? null
    };

    const result = await this.insertRecord(callData);
    return result.data;
  }

  async endCall(callId: string): Promise<Call | null> {
    const updateData = {
      endDate: new Date().toISOString()
    };

    const result = await this.updateRecordById(callId, updateData);
    return result.data;
  }

  async getCompletedCalls(): Promise<Call[]> {
    // Prefer camelCase columns; use NOT NULL operator which is reliable in PostgREST
    const camel = await this.executeCustomQuery((q: any) =>
      q
        .select('*')
        .not('endDate', 'is', null)
        .order('endDate', { ascending: false })
    );
    if (camel.data && camel.data.length > 0) return camel.data as Call[];

    // Fallback to snake_case
    const snake = await this.executeCustomQuery((q: any) =>
      q
        .select('*')
        .not('end_date', 'is', null)
        .order('end_date', { ascending: false })
    );
    return (snake.data as Call[]) || [];
  }

  async getUserCompletedCalls(userId: string): Promise<Call[]> {
    // First try camelCase columns
    const camel = await this.executeCustomQuery((q: any) =>
      q
        .select('*')
        .not('endDate', 'is', null)
        .eq('userId', userId)
        .order('endDate', { ascending: false })
    );
    if (camel.data && camel.data.length > 0) return camel.data as Call[];

    // Fallback to snake_case columns
    const snake = await this.executeCustomQuery((q: any) =>
      q
        .select('*')
        .not('end_date', 'is', null)
        .eq('user_id', userId)
        .order('end_date', { ascending: false })
    );
    return (snake.data as Call[]) || [];
  }

  async getCallsByDateRange(startDate: string, endDate: string): Promise<Call[]> {
    const result = await this.selectAllRecords({
      filters: [
        { column: 'startDateTime', operator: 'gte', value: startDate },
        { column: 'startDateTime', operator: 'lte', value: endDate }
      ],
      orderBy: 'startDateTime',
      ascending: false
    });

    return result.data || [];
  }

  async deleteCall(callId: string): Promise<boolean> {
    const result = await this.deleteRecordById(callId);
    return result.error === null;
  }

  async getCallById(callId: string): Promise<Call | null> {
    const result = await this.selectRecordById(callId);
    return result.data;
  }

  async getCallsWithInfo(centerId?: string, userEmail?: string, startDate?: string, endDate?: string): Promise<any[]> {
    let query = this.client.from('CallWithInfo').select('*');
    if (centerId) {
      query = query.eq('center_id', centerId);
    }
    if (userEmail) {
      query = query.ilike('user_email', `%${userEmail}%`);
    }
    if (startDate) {
      query = query.gte('created_at', startDate);
    }
    if (endDate) {
      // Add one day or use a more precise comparison if needed, 
      // but usually for date pickers, we want the end of the day.
      // If endDate is YYYY-MM-DD, we want it to include that day.
      query = query.lte('created_at', `${endDate}T23:59:59.999Z`);
    }
    const { data, error } = await query.order('created_at', { ascending: false }).limit(100);
    if (error) throw error;
    return data || [];
  }

  async getLogsForCall(callId: string): Promise<any[]> {
    const { data, error } = await this.client
      .from('Log')
      .select('*')
      .eq('call_id', callId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  }

  async reportErrors(callId: string, errors: string): Promise<boolean> {
    const { error } = await this.client
      .from('Call')
      .update({ reported_errors: errors })
      .eq('id', callId);
    if (error) throw error;
    return true;
  }
}
